import React from 'react';
import type { ReactElement } from 'react';
import {
  Document,
  Page,
  Text,
  View,
  StyleSheet,
  Image,
  Font,
} from '@react-pdf/renderer';
import type {
  FullUser,
  InterpretationWithRelations,
  SupportedLanguage,
} from '@elyope/db';

// Define styles
const styles = StyleSheet.create({
  page: {
    padding: 40,
    paddingBottom: 80,
    fontSize: 11,
    fontFamily: 'Calibri',
    fontWeight: 400,
    lineHeight: 1.3,
  },
  title: {
    fontSize: 16,
    fontFamily: 'Poppins',
    fontWeight: 'bold',
    textAlign: 'center',
    marginBottom: 20,
  },
  patientBlock: {
    marginBottom: 20,
    padding: 5,
    border: '1 solid #e0e0e0',
    borderRadius: 4,
  },
  patientRow: {
    flexDirection: 'row',
    marginBottom: 4,
  },
  patientLabel: {
    fontSize: 11,
    fontWeight: 'bold',
    width: 140,
  },
  patientValue: {
    fontSize: 11,
  },
  section: {
    marginBottom: 15,
  },
  sectionTitle: {
    fontSize: 12,
    fontWeight: 'bold',
    marginBottom: 8,
    borderBottom: '1 solid #e0e0e0',
    paddingBottom: 4,
  },
  text: {
    fontSize: 11,
    marginBottom: 6,
    textAlign: 'justify',
  },
  resultItem: {
    marginBottom: 10,
    alignItems: 'flex-start',
  },
  resultImageContainer: {
    width: '100%',
    flexDirection: 'column',
    justifyContent: 'flex-start',
    alignItems: 'flex-start',
    alignSelf: 'flex-start',
  },
  resultImage: {
    width: '80%',
    height: 300,
    objectFit: 'contain',
    alignSelf: 'center',
  },
  resultDescription: {
    fontSize: 10,
    marginTop: 5,
  },
  conclusion: {
    marginTop: 20,
    padding: 5,
    backgroundColor: '#f5f5f5',
    borderRadius: 4,
  },
  conclusionTitle: {
    fontSize: 12,
    fontWeight: 'bold',
    marginBottom: 8,
  },
  footer: {
    position: 'absolute',
    bottom: 30,
    left: 40,
    right: 40,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-start',
    borderTop: '1 solid #e0e0e0',
    paddingTop: 8,
  },
  footerText: {
    fontSize: 9,
    color: '#666',
    marginRight: 6,
  },
  footerLogo: {
    height: 7,
  },
  interpretedAt: {
    fontSize: 9,
    color: '#666',
    marginTop: 10,
  },
});

export interface InterpretationPDFTranslations {
  title: string;
  for_patient: string;
  section_patient: string;
  section_anamnese?: string;
  section_technique: string;
  section_lecture: string;
  section_results: string;
  section_conclusion: string;
  interpreted_at: string;
  exam_ref: string;
  footer_transmitted_by?: string;
  patient_name?: string;
  patient_species?: string;
  patient_breed?: string;
  patient_sex?: string;
  patient_birth_date?: string;
  patient_identification?: string;
  sex_male?: string;
  sex_female?: string;
  sex_unknown?: string;
  not_provided?: string;
}

export interface InterpretationPDFFonts {
  regularFont: string;
  boldFont: string;
  italicFont?: string;
  boldItalicFont?: string;
  titleFont?: string;
}

export interface AnimalInfo {
  name?: string;
  species?: string;
  breed?: string;
  sex?: string;
  birthDate?: string;
  identificationNumber?: string;
}

export interface InterpretationPDFProps {
  interpretation: InterpretationWithRelations;
  examNumber?: string;
  patientName?: string;
  animalInfo?: AnimalInfo;
  logoUrl?: string;
  interpretor?: FullUser;
  interpreterSignature?: string | null;
  translations: InterpretationPDFTranslations;
  fonts: InterpretationPDFFonts;
}

export interface InterpretationPDFWrapperProps {
  interpretation: InterpretationWithRelations;
  examNumber?: string;
  patientName?: string;
  animalInfo?: AnimalInfo;
  logoUrl?: string;
  interpretor?: FullUser;
  interpreterSignature?: string | null;
  locale?: SupportedLanguage;
}

export const registerInterpretationPDFFonts = (
  fonts: InterpretationPDFFonts
) => {
  // Register Calibri as main font
  const calibriFonts: Array<{
    src: string;
    fontWeight: number | 'bold';
    fontStyle: 'normal' | 'italic';
  }> = [
    {
      src: fonts.regularFont,
      fontWeight: 400,
      fontStyle: 'normal',
    },
  ];

  // If we have a separate bold font, use it; otherwise react-pdf will simulate bold
  if (fonts.boldFont && fonts.boldFont !== fonts.regularFont) {
    calibriFonts.push({
      src: fonts.boldFont,
      fontWeight: 'bold',
      fontStyle: 'normal',
    });
  }

  if (fonts.italicFont) {
    calibriFonts.push({
      src: fonts.italicFont,
      fontWeight: 400,
      fontStyle: 'italic',
    });
  }

  if (fonts.boldItalicFont) {
    calibriFonts.push({
      src: fonts.boldItalicFont,
      fontWeight: 400,
      fontStyle: 'italic',
    });
  }

  Font.register({
    family: 'Calibri',
    fonts: calibriFonts,
  });

  // Register Poppins for title
  if (fonts.titleFont) {
    Font.register({
      family: 'Poppins',
      fonts: [
        {
          src: fonts.titleFont,
          fontWeight: 'bold',
          fontStyle: 'normal',
        },
      ],
    });
  }
};

export const InterpretationPDF = ({
  interpretation,
  animalInfo,
  logoUrl,
  interpretor,
  interpreterSignature,
  translations,
}: InterpretationPDFProps): ReactElement => {
  const isEmptyHtml = (html?: string | null) =>
    !html?.trim() || html === '<br>';

  // Parse HTML and convert to react-pdf Text components
  const parseHTML = (html: string, keyPrefix = 'html'): React.ReactNode[] => {
    if (!html || html === '<br>') return [];

    const decodeEntities = (text: string): string => {
      const entityMap: Record<string, string> = {
        '&nbsp;': ' ',
        '&amp;': '&',
        '&lt;': '<',
        '&gt;': '>',
        '&quot;': '"',
        '&#39;': "'",
        '&apos;': "'",
      };
      return text.replace(/&[#\w]+;/g, (entity) => {
        return entityMap[entity] || entity;
      });
    };

    const result: React.ReactNode[] = [];
    let keyCounter = 0;
    const getKey = () => `${keyPrefix}-${keyCounter++}`;

    const processedHtml = html.replace(/<br\s*\/?>/gi, '\n');

    let currentPos = 0;
    const tagRegex = /<(\/?)(\w+)([^>]*)>/g;
    let match;

    type TextStyle = {
      fontWeight?: 'bold' | 'normal';
      fontStyle?: 'italic' | 'normal';
      textDecoration?: 'underline' | 'none';
    };
    const tagStack: Array<{
      tag: string;
      start: number;
      style?: TextStyle;
    }> = [];
    const textParts: Array<{
      text: string;
      styles: TextStyle[];
    }> = [];

    while ((match = tagRegex.exec(processedHtml)) !== null) {
      const [fullMatch, isClosing, tagName] = match;
      const tagStart = match.index;
      const tagEnd = tagStart + fullMatch.length;

      if (tagStart > currentPos) {
        const text = processedHtml.substring(currentPos, tagStart);
        if (text) {
          textParts.push({
            text: decodeEntities(text),
            styles: tagStack
              .map((t) => t.style)
              .filter((s): s is TextStyle => s !== undefined && s !== null),
          });
        }
      }

      const lowerTag = tagName.toLowerCase();

      if (isClosing) {
        tagStack.pop();
      } else {
        let style: TextStyle | null = null;

        switch (lowerTag) {
          case 'b':
          case 'strong':
            style = { fontWeight: 'bold' };
            break;
          case 'i':
          case 'em':
            style = { fontStyle: 'italic' };
            break;
          case 'u':
            style = { textDecoration: 'underline' };
            break;
          case 'span':
          case 'p':
          case 'div':
            break;
        }

        if (style) {
          tagStack.push({ tag: lowerTag, start: tagStart, style });
        } else if (
          lowerTag !== 'span' &&
          lowerTag !== 'p' &&
          lowerTag !== 'div'
        ) {
          tagStack.push({ tag: lowerTag, start: tagStart });
        }
      }

      currentPos = tagEnd;
    }

    if (currentPos < processedHtml.length) {
      const text = processedHtml.substring(currentPos);
      if (text) {
        textParts.push({
          text: decodeEntities(text),
          styles: tagStack
            .map((t) => t.style)
            .filter((s): s is TextStyle => s !== undefined && s !== null),
        });
      }
    }

    if (textParts.length === 0) {
      const plainText = decodeEntities(processedHtml);
      if (plainText.trim()) {
        return [<Text key={getKey()}>{plainText}</Text>];
      }
      return [];
    }

    textParts.forEach((part) => {
      if (!part.text.trim() && part.text !== '\n') return;

      const combinedStyle = part.styles.reduce(
        (acc, style) => ({ ...acc, ...style }),
        {}
      );

      if (Object.keys(combinedStyle).length > 0) {
        result.push(
          <Text key={getKey()} style={combinedStyle}>
            {part.text}
          </Text>
        );
      } else {
        result.push(<Text key={getKey()}>{part.text}</Text>);
      }
    });

    return result.length > 0
      ? result
      : [<Text key={getKey()}>{decodeEntities(html)}</Text>];
  };

  const renderText = (html?: string | null) => {
    if (!html) return null;

    const hasHTML = /<[^>]+>/.test(html);

    if (hasHTML) {
      const parsed = parseHTML(html);

      const lines: React.ReactNode[][] = [];
      let currentLine: React.ReactNode[] = [];

      parsed.forEach((node) => {
        if (
          React.isValidElement(node) &&
          typeof (node.props as { children?: unknown })?.children ===
            'string' &&
          (node.props as { children?: unknown }).children === '\n'
        ) {
          if (currentLine.length > 0) {
            lines.push(currentLine);
            currentLine = [];
          }
        } else {
          currentLine.push(node);
        }
      });

      if (currentLine.length > 0) {
        lines.push(currentLine);
      }

      if (lines.length === 0 && parsed.length > 0) {
        lines.push(parsed);
      }

      return lines.map((lineNodes, index) => (
        <Text key={index} style={styles.text}>
          {lineNodes}
        </Text>
      ));
    } else {
      return html.split('\n').map((line, index) => (
        <Text key={index} style={styles.text}>
          {line}
        </Text>
      ));
    }
  };

  const getSexLabel = (sex?: string): string => {
    if (!sex) return translations.sex_unknown || 'Non renseigné';
    switch (sex) {
      case 'MALE':
        return translations.sex_male || 'Mâle';
      case 'FEMALE':
        return translations.sex_female || 'Femelle';
      default:
        return translations.sex_unknown || 'Non renseigné';
    }
  };

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        {/* Title - centered, Poppins Bold */}
        <Text style={styles.title}>{translations.title}</Text>

        {/* Patient structured block */}
        {animalInfo && (
          <View style={styles.patientBlock}>
            <Text
              style={[
                styles.sectionTitle,
                { borderBottom: 'none', paddingBottom: 0, marginBottom: 6 },
              ]}
            >
              {translations.section_patient}
            </Text>
            {animalInfo.name && (
              <View style={styles.patientRow}>
                <Text style={styles.patientLabel}>
                  {translations.patient_name || 'Nom'} :
                </Text>
                <Text style={styles.patientValue}>{animalInfo.name}</Text>
              </View>
            )}
            {animalInfo.species && (
              <View style={styles.patientRow}>
                <Text style={styles.patientLabel}>
                  {translations.patient_species || 'Espèce'} :
                </Text>
                <Text style={styles.patientValue}>{animalInfo.species}</Text>
              </View>
            )}
            {animalInfo.breed && (
              <View style={styles.patientRow}>
                <Text style={styles.patientLabel}>
                  {translations.patient_breed || 'Race'} :
                </Text>
                <Text style={styles.patientValue}>{animalInfo.breed}</Text>
              </View>
            )}
            <View style={styles.patientRow}>
              <Text style={styles.patientLabel}>
                {translations.patient_sex || 'Sexe'} :
              </Text>
              <Text style={styles.patientValue}>
                {getSexLabel(animalInfo.sex)}
              </Text>
            </View>
            <View style={styles.patientRow}>
              <Text style={styles.patientLabel}>
                {translations.patient_birth_date || 'Date de naissance'} :
              </Text>
              <Text style={styles.patientValue}>
                {animalInfo.birthDate ||
                  translations.not_provided ||
                  'Non renseigné'}
              </Text>
            </View>
            {animalInfo.identificationNumber && (
              <View style={styles.patientRow}>
                <Text style={styles.patientLabel}>
                  {translations.patient_identification || 'N° de ref.'} :
                </Text>
                <Text style={styles.patientValue}>
                  {animalInfo.identificationNumber}
                </Text>
              </View>
            )}
          </View>
        )}

        {/* Anamnèse Section (was "patient" field in interpretation) */}
        {!isEmptyHtml(interpretation.anamnese) && (
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>
              {translations.section_anamnese || 'Anamnèse'}
            </Text>
            {renderText(interpretation.anamnese)}
          </View>
        )}

        {/* Technique Section */}
        {!isEmptyHtml(interpretation.technique) && (
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>
              {translations.section_technique}
            </Text>
            {renderText(interpretation.technique)}
          </View>
        )}

        {/* Paramètres de lecture Section */}

        {!isEmptyHtml(interpretation.lecture) && (
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>
              {translations.section_lecture}
            </Text>
            {interpretation.lecture}
          </View>
        )}

        {/* Results Section */}
        {!isEmptyHtml(interpretation.resultDescription) && (
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>
              {translations.section_results}
            </Text>
            {renderText(interpretation.resultDescription)}
          </View>
        )}

        {/* Results with Images */}
        {interpretation.results?.length > 0 && (
          <View style={styles.section}>
            {/* Titre + 1er résultat ensemble */}
            <View>
              {(() => {
                const r = [...interpretation.results].sort(
                  (a, b) => (a.order || 0) - (b.order || 0)
                )[0];

                return (
                  <View
                    style={styles.resultItem}
                    wrap={false}
                    minPresenceAhead={360}
                  >
                    {r.image &&
                    (r.image.startsWith('data:') ||
                      r.image.startsWith('http')) ? (
                      <View style={styles.resultImageContainer}>
                        {r.description && (
                          <View style={styles.resultDescription}>
                            {renderText(r.description)}
                          </View>
                        )}
                        <Image
                          src={r.image}
                          style={styles.resultImage}
                          cache={false}
                        />
                      </View>
                    ) : (
                      r.description && (
                        <View style={styles.resultDescription}>
                          {renderText(r.description)}
                        </View>
                      )
                    )}
                  </View>
                );
              })()}
            </View>

            {/* Le reste */}
            {[...interpretation.results]
              .sort((a, b) => (a.order || 0) - (b.order || 0))
              .slice(1)
              .map((result, index) => (
                <View
                  key={result.id || index}
                  style={styles.resultItem}
                  wrap={false}
                  minPresenceAhead={360} // plus réaliste que 50 si image
                >
                  {result.image &&
                  (result.image.startsWith('data:') ||
                    result.image.startsWith('http')) ? (
                    <View style={styles.resultImageContainer}>
                      {result.description && (
                        <View style={styles.resultDescription}>
                          {renderText(result.description)}
                        </View>
                      )}
                      <Image
                        src={result.image}
                        style={styles.resultImage}
                        cache={false}
                      />
                    </View>
                  ) : (
                    result.description && (
                      <View style={styles.resultDescription}>
                        {renderText(result.description)}
                      </View>
                    )
                  )}
                </View>
              ))}
          </View>
        )}

        {/* Conclusion Section */}
        {interpretation.conclusion && (
          <View style={styles.conclusion} wrap={false}>
            <Text style={styles.conclusionTitle}>
              {translations.section_conclusion}
            </Text>
            {renderText(interpretation.conclusion)}
            <View style={{ marginTop: 20 }} />
            {interpreterSignature
              ? interpreterSignature.split('\n').map((line, i) => (
                  <Text key={i} style={styles.text}>
                    {line}
                  </Text>
                ))
              : interpretor && interpretor.fullName
              ? renderText(interpretor.fullName)
              : renderText(interpretor?.email)}
          </View>
        )}

        {/* Interpreted at */}
        <Text style={styles.interpretedAt}>
          {translations.interpreted_at}{' '}
          {interpretation.interpretedAt
            ? new Date(interpretation.interpretedAt).toLocaleDateString()
            : ''}
        </Text>

        {/* Footer: "EEG transmis par" + Elyope logo */}
        <View style={styles.footer}>
          <Text style={styles.footerText}>
            {translations.footer_transmitted_by || 'EEG transmis par'}
          </Text>
          {logoUrl && (
            // eslint-disable-next-line jsx-a11y/alt-text -- react-pdf Image doesn't support alt prop
            <Image src={logoUrl} style={styles.footerLogo} cache={false} />
          )}
        </View>
      </Page>
    </Document>
  );
};
